use '@use-gpu/wgsl/codec/octahedral'::{ decodeOctahedral, wrapOctahedral, decodeHemiOctahedral };
use '@use-gpu/wgsl/use/types'::{ Light };

@link fn getLight() -> Light;
@link fn getDepth(xy: vec2<u32>, i: u32, l: u32) -> f32;

@export fn debugShadowMapPosition(i: u32) -> vec4<f32> {
  let l = getLight();

  let kind = l.kind;

  let matrix = l.matrix;
  let itfov2 = l.opts.z;

  let layer = l.shadowMap;
  let blur = l.shadowBlur;
  let znf = l.shadowDepth;
  let mapping = l.shadowUV;

  let size = (mapping.zw - mapping.xy) * SHADOW_PAGE;
  let modulus = u32(size.x);

  let x = i % modulus;
  let y = i / modulus;

  let xym = vec2<u32>(x, y);
  let uvm = (vec2<f32>(xym) + .5) / size;

  let uv = mix(mapping.xy, mapping.zw, uvm);
  let xy = vec2<u32>(uv * SHADOW_PAGE);

  let depth = getDepth(xy, u32(layer), 0u);

  if (kind == 2) {
    return mapOrtho(uvm, depth, matrix);
  }
  if (kind == 3) {
    return mapOmni(uvm, depth, matrix, znf, size.x, blur);
  }
  if (kind == 4) {
    return mapHemi(uvm, depth, matrix, znf);
  }
  if (kind == 5) {
    return mapSpot(uvm, depth, matrix, znf, itfov2);
  }

  return vec4<f32>(0.0);
}

fn mapOrtho(uv: vec2<f32>, depth: f32, matrix: mat4x4<f32>) -> vec4<f32> {
  // Reverse Z = View space (ortho)
  // 1 - depth = -pos.z
  let linearZ = depth - 1.0;

  // Flip Y
  let object = vec4<f32>(vec2<f32>(uv.x, 1.0 - uv.y) * 2.0 - 1.0, linearZ, 1.0);
  let world = matrix * object;

  return world;
}

fn mapOmni(uv: vec2<f32>, depth: f32, matrix: mat4x4<f32>, znf: vec2<f32>, size: f32, blur: i32) -> vec4<f32> {
  // Reverse Z = View space (perspective)
  let linearZ = znf.y / (depth - znf.x);

  let scale = size / (size - (f32(blur) * 2.0));
  let uvm = wrapOctahedral((uv * 2.0 - 1.0) * scale);
  let xyz = decodeOctahedral(uvm).yzx;

  let object = vec4<f32>(xyz * linearZ, 1.0);
  let world = matrix * object;

  return world;
}

fn mapHemi(uv: vec2<f32>, depth: f32, matrix: mat4x4<f32>, znf: vec2<f32>) -> vec4<f32> {
  // Reverse Z = View space (perspective)
  let linearZ = znf.y / (depth - znf.x);

  let uvm = (uv * 2.0 - 1.0);
  let xyz = decodeHemiOctahedral(uvm).yzx;

  let object = vec4<f32>(xyz * linearZ, 1.0);
  let world = matrix * object;

  return world;
}

fn mapSpot(uv: vec2<f32>, depth: f32, matrix: mat4x4<f32>, znf: vec2<f32>, itfov2: f32) -> vec4<f32> {
  // Reverse Z = View space (perspective)
  let linearZ = znf.y / (depth - znf.x);

  let uvm = (uv * 2.0 - 1.0) / itfov2;
  let xyz = vec3<f32>(uvm.x, uvm.y, -1.0);

  let object = vec4<f32>(xyz * linearZ, 1.0);
  let world = matrix * object;

  return world;
}
